import React, { useEffect, useRef, useState } from 'react';
import { aboutText } from '../data/mock';

const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [parallaxOffset, setParallaxOffset] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Parallax effect on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const scrollProgress = -rect.top / (rect.height + window.innerHeight);
        setParallaxOffset(scrollProgress * 50);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const paragraphs = aboutText.split('\n\n');

  return (
    <section
      id="about"
      ref={sectionRef}
      className="py-14 md:py-20 bg-white dark:bg-gray-950 transition-colors duration-500 relative overflow-hidden"
    >
      {/* Background decoration with parallax */}
      <div
        className="absolute top-0 right-0 w-96 h-96 bg-gray-100 dark:bg-gray-800 rounded-full blur-3xl opacity-30 animate-morph transition-transform duration-300 ease-out"
        style={{ transform: `translateY(${parallaxOffset}px)` }}
      />

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
        {/* Full width layout */}
        <div className="w-full">
          <h2
            className={`text-4xl md:text-5xl lg:text-6xl font-extralight text-black dark:text-white mb-4 transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0 scale-100' : 'opacity-0 -translate-x-12 scale-95'}`}
          >
            About Me
          </h2>

          {/* Animated gradient underline with flowing shimmer effect */}
          <div className="relative h-1 mt-4 mb-12 overflow-hidden">
            <div
              className={`absolute inset-0 bg-gradient-to-r from-gray-800 via-gray-400 to-gray-200 dark:from-white dark:via-gray-400 dark:to-gray-700 transition-all duration-1000 ${isVisible ? 'w-32 opacity-100' : 'w-0 opacity-0'}`}
              style={{ transitionDelay: '200ms' }}
            />
            {/* Animated shimmer overlay */}
            <div
              className={`absolute inset-0 w-32 overflow-hidden ${isVisible ? 'opacity-100' : 'opacity-0'}`}
              style={{ transitionDelay: '700ms' }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer" />
            </div>
          </div>

          {/* Content - Full width */}
          <div className="space-y-6 max-w-none">
            {paragraphs.map((paragraph, index) => (
              <p
                key={index}
                className={`text-[15px] md:text-[17px] font-light text-gray-600 dark:text-gray-400 leading-relaxed transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}
                style={{ transitionDelay: `${(index + 1) * 150}ms` }}
              >
                {paragraph}
              </p>
            ))}
          </div>

          {/* Decorative Line */}
          <div
            className={`mt-12 h-px bg-gray-200 dark:bg-gray-800 transition-all duration-1000 ${isVisible ? 'w-full opacity-100' : 'w-0 opacity-0'}`}
            style={{ transitionDelay: '500ms' }}
          />
        </div>
      </div>
    </section>
  );
};

export default About;
